import { zodResolver } from '@hookform/resolvers/zod';
import {
	CodelistRecord,
	createCodelistRecordSchema
} from 'features/codelist/domain/codelistRecordSchema';
import { Button } from 'features/components/Button';
import { Input } from 'features/entry-form-v2/component/Input';
import { useLanguage } from 'features/hooks/useLanguage';
import { useForm } from 'react-hook-form';
import { CODELIST_SIDEBAR_DICTIONARY } from '../Dictionary';
import { LanguageType } from 'types';
import { usePrompt } from '../../../../hooks/navigation';
import { useProjectId } from '../../../hooks/useProjectId';
import { useSearchRecordsQuery } from '../../data/useSearchRecordsMutation';
import { HelpCircleIcon } from 'lucide-react';
import { Dialog } from '../../../entry-form-v2/component/Dialog';
import { useEffect, useState } from 'react';

export const CodelistForm = ({
	onSubmit,
	initialCodelist,
	onCancelClicked,
	loading,
	error
}: {
	onSubmit: (data: CodelistRecord) => void;
	initialCodelist?: CodelistRecord;
	onCancelClicked: () => void;
	loading: boolean;
	error?: string;
}) => {
	const language = useLanguage();
	const localTranslation = DICTIONARY[language];
	const sharedTranslation = CODELIST_SIDEBAR_DICTIONARY[language];

	const form = useForm<CodelistRecord>({
		resolver: zodResolver(createCodelistRecordSchema(language)),
		defaultValues: initialCodelist
	});

	const isDirty = form.formState.isDirty;
	usePreventNavigationIfFormIsDirty(isDirty);

	const ssnAlreadyInUse = useIsInvalidSsn({
		ssn: form.watch('ssn'),
		initialCodelist,
		isDirty
	});

	const customIdAlreadyInUse = useIsInvalidCustomId({
		customId: form.watch('customId'),
		initialCodelist,
		isDirty
	});

	return (
		<form
			onSubmit={form.handleSubmit(data => {
				if (customIdAlreadyInUse) {
					form.setFocus('customId');
					return;
				}

				onSubmit(data);
			})}
			className="flex flex-col gap-4"
		>
			<div className="flex flex-col gap-1">
				<Input
					label={sharedTranslation.customId.label}
					id="customId"
					autoFocus
					{...form.register('customId')}
					error={form.formState.errors.customId?.message}
				/>
				{customIdAlreadyInUse && (
					<p className="text-error-500 text-sm font-bold">
						{DIALOG_DICTIONARY[language].customIdAlreadyInUse}
					</p>
				)}
			</div>

			<div className="flex flex-col gap-1">
				<Input label={sharedTranslation.ssn.label} id="ssn" {...form.register('ssn')} />
				{ssnAlreadyInUse && <SsnHelpDialog />}
			</div>

			<Input
				label={sharedTranslation.firstName.label}
				id="firstName"
				{...form.register('firstName')}
				error={form.formState.errors.firstName?.message}
			/>

			<Input
				label={sharedTranslation.lastName.label}
				id="lastName"
				{...form.register('lastName')}
				error={form.formState.errors.lastName?.message}
			/>

			<Input
				label={sharedTranslation.dateOfBirth.label}
				id="dateOfBirth"
				{...form.register('dateOfBirth')}
				type="date"
				error={form.formState.errors.dateOfBirth?.message}
				max={new Date().toISOString().split('T')[0]}
			/>

			<Input
				label={sharedTranslation.mobilePhone.label}
				id="mobilePhone"
				{...form.register('mobilePhone')}
				error={form.formState.errors.mobilePhone?.message}
			/>

			<Input
				label={sharedTranslation.email.label}
				id="email"
				{...form.register('email')}
				error={form.formState.errors.email?.message}
			/>

			<Input
				label={sharedTranslation.address.label}
				id="address"
				{...form.register('address')}
				error={form.formState.errors.address?.message}
			/>

			<Input
				label={sharedTranslation.postalCode.label}
				id="postalCode"
				{...form.register('postalCode')}
				error={form.formState.errors.postalCode?.message}
			/>

			<Input
				label={sharedTranslation.postalPlace.label}
				id="postalPlace"
				{...form.register('postalPlace')}
				error={form.formState.errors.postalPlace?.message}
			/>

			<Input
				label={sharedTranslation.country.label}
				id="country"
				{...form.register('country')}
				error={form.formState.errors.country?.message}
			/>

			{error && <p className="text-red-500 text-sm font-bold text-center my-2">{error}</p>}

			<div className="flex flex-col gap-4 justify-end">
				<Button
					type="submit"
					variant="primary"
					text={localTranslation.submitButton}
					loading={loading}
				/>

				<Button
					type="button"
					variant="secondary"
					text={localTranslation.cancelButton}
					onClick={onCancelClicked}
				/>
			</div>
		</form>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		submitButton: 'Save',
		cancelButton: 'Cancel',

		ssnAlreadyInUse: 'Personal identification number already in use'
	},
	[LanguageType.Norwegian]: {
		submitButton: 'Lagre',
		cancelButton: 'Avbryt',

		ssnAlreadyInUse: 'Personnummeret allerede i bruk'
	}
} as const;

const usePreventNavigationIfFormIsDirty = (isDirty: boolean) => {
	const language = useLanguage();

	usePrompt(() => {
		if (isDirty) {
			// Show confirmation dialog and only proceed if user confirms
			return window.confirm(DIALOG_DICTIONARY[language].message);
		}
		// If form is clean, allow navigation
		return true;
	}, isDirty);
};

const DIALOG_DICTIONARY = {
	[LanguageType.English]: {
		message: 'You have unsaved codelist changes. Are you sure you want to leave this page?',
		customIdAlreadyInUse: 'Codelist ID already in use',
		ssnAlreadyInUse: 'Personal identification number already in use',
		ssnAlreadyInUseDescription:
			'The personal identification number is already in use in another codelist. We show this warning so you can check if it is an error. If it is intentional, you can ignore this warning.'
	},
	[LanguageType.Norwegian]: {
		message:
			'Du har ulagrede endringer i kodelisten. Er du sikker på at du vil forlate denne siden?',
		customIdAlreadyInUse: 'Kodeliste-ID er allerede i bruk',
		ssnAlreadyInUse: 'Personnummeret er allerede i bruk',
		ssnAlreadyInUseDescription:
			'Personnummeret er allerede i bruk i en annen kodeliste. Vi viser denne advarselen slik at du kan sjekke om det er en feil. Dersom det er  kan du se bort ifra denne advarselen.'
	}
} as const;

const useIsInvalidSsn = (args: {
	ssn: string | undefined;
	initialCodelist?: CodelistRecord;
	isDirty: boolean;
}) => {
	const projectId = useProjectId();

	const debouncedSsn = useDebouncedValue(args.ssn);

	const existingSsnRecordMutation = useSearchRecordsQuery(
		{
			filters: [{ key: 'ssn', value: debouncedSsn!, match: 'EXACT_CASE_INSENSITIVE' }],
			projectId
		},
		{
			enabled: !!debouncedSsn && args.isDirty
		}
	);

	const isInitialSsn = args.initialCodelist?.ssn === args.ssn;
	const ssnAlreadyInUse =
		!isInitialSsn &&
		existingSsnRecordMutation.data &&
		existingSsnRecordMutation.data.records.length > 0;

	return ssnAlreadyInUse;
};

const useIsInvalidCustomId = (args: {
	customId: string | undefined;
	initialCodelist?: CodelistRecord;
	isDirty: boolean;
}) => {
	const projectId = useProjectId();
	const debouncedCustomId = useDebouncedValue(args.customId);

	const existingCustomIdRecordMutation = useSearchRecordsQuery(
		{
			filters: [
				{ key: 'customId', value: debouncedCustomId!, match: 'EXACT_CASE_INSENSITIVE' }
			],
			projectId
		},
		{
			enabled: !!debouncedCustomId && args.isDirty
		}
	);

	const isInitialCustomId = args.initialCodelist?.customId === args.customId;
	const customIdAlreadyInUse =
		!isInitialCustomId &&
		existingCustomIdRecordMutation.data &&
		existingCustomIdRecordMutation.data.records.length > 0;

	return customIdAlreadyInUse;
};

const SsnHelpDialog = () => {
	const [open, setOpen] = useState(false);

	const language = useLanguage();
	const translation = DIALOG_DICTIONARY[language];

	return (
		<>
			<button
				onClick={() => setOpen(true)}
				className="flex gap-2 items-center rounded-md hover:bg-gray-300"
				type="button"
			>
				<HelpCircleIcon className="text-warning-800 size-6" />

				<p className="text-warning-800 text-sm font-bold">{translation.ssnAlreadyInUse}</p>
			</button>

			<Dialog open={open} onClose={() => setOpen(false)} title={translation.ssnAlreadyInUse}>
				<div className="flex flex-col gap-2 w-[600px]">
					<p className="text-sm">{translation.ssnAlreadyInUseDescription}</p>
				</div>
			</Dialog>
		</>
	);
};

const useDebouncedValue = <T,>(value: T | undefined, delayInMs = 2000) => {
	const [debouncedValue, setDebouncedValue] = useState<T>();

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delayInMs);

		return () => clearTimeout(timer);
	}, [value, delayInMs]);

	return debouncedValue;
};
